/**
 * Phase-5 worker identity creation dialog.
 *
 * Flow:
 *   1. User enters name / description / type and selects allowed capabilities.
 *   2. On submit, POST /workers → { worker, client_id, client_secret }.
 *   3. The dialog swaps into the one-shot reveal panel with copy buttons
 *      and the warning "Save this; we won't show it again."
 *
 * UI vocabulary rules (per 47l §13): no "NATS", no subject strings,
 * no account names. `client_id` is rendered as "Worker ID" and
 * `client_secret` as "Worker Secret".
 */

import { useEffect, useMemo, useState } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { AlertTriangle, Check, Copy, Loader2, Server, X } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
   useCreateWorker,
   type WorkerSecretReveal,
   type WorkerType,
} from '@/hooks/use-workers';

interface CreateWorkerDialogProps {
   isOpen: boolean;
   onClose: () => void;
   onCreated: () => void;
}

type Phase = 'form' | 'success';

const WORKER_TYPE_OPTIONS: Array<{ value: WorkerType; label: string; hint: string }> = [
   { value: 'third_party', label: 'Third-party', hint: 'A worker run by an external partner or vendor.' },
   { value: 'self_hosted', label: 'Self-hosted', hint: 'A worker your team runs on your own infrastructure.' },
   { value: 'platform', label: 'Platform', hint: 'A worker managed by the more0 platform.' },
];

interface CapabilityOption {
   id: string;
   name: string;
   app_key?: string;
}

async function fetchCapabilityOptions(): Promise<CapabilityOption[]> {
   const res = await api.get('/capabilities', { params: { type: 'tool', include_entitled: 'true' } });
   const payload = res.data as unknown;
   const list = Array.isArray(payload)
      ? payload
      : Array.isArray((payload as { data?: unknown[] })?.data)
        ? (payload as { data: unknown[] }).data
        : [];
   const out: CapabilityOption[] = [];
   for (const raw of list) {
      const c = raw as { id?: string; name?: string; app_key?: string };
      if (!c?.id || !c?.name) continue;
      const entry: CapabilityOption = { id: c.id, name: c.name };
      if (c.app_key) entry.app_key = c.app_key;
      out.push(entry);
   }
   return out;
}

export default function CreateWorkerDialog({
   isOpen,
   onClose,
   onCreated,
}: CreateWorkerDialogProps) {
   const [phase, setPhase] = useState<Phase>('form');
   const [name, setName] = useState('');
   const [description, setDescription] = useState('');
   const [workerType, setWorkerType] = useState<WorkerType>('third_party');
   const [capabilityIds, setCapabilityIds] = useState<Set<string>>(new Set());
   const [capabilityOptions, setCapabilityOptions] = useState<CapabilityOption[]>([]);
   const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
   const [credentials, setCredentials] = useState<WorkerSecretReveal | null>(null);
   const [error, setError] = useState<string | null>(null);
   const [copiedId, setCopiedId] = useState(false);
   const [copiedSecret, setCopiedSecret] = useState(false);

   const createWorker = useCreateWorker();

   useEffect(() => {
      if (!isOpen) return;
      setPhase('form');
      setName('');
      setDescription('');
      setWorkerType('third_party');
      setCapabilityIds(new Set());
      setCredentials(null);
      setError(null);
      setCopiedId(false);
      setCopiedSecret(false);
      setCapabilitiesLoading(true);
      fetchCapabilityOptions()
         .then(setCapabilityOptions)
         .catch(() => setCapabilityOptions([]))
         .finally(() => setCapabilitiesLoading(false));
   }, [isOpen]);

   const canSubmit = useMemo(() => name.trim().length > 0 && !createWorker.isPending, [
      name,
      createWorker.isPending,
   ]);

   const handleSubmit = async () => {
      setError(null);
      const trimmed = name.trim();
      if (!trimmed) {
         setError('Name is required');
         return;
      }
      try {
         const res = await createWorker.mutateAsync({
            name: trimmed,
            description: description.trim() || undefined,
            worker_type: workerType,
            allowed_capability_ids: Array.from(capabilityIds),
         });
         setCredentials(res);
         setPhase('success');
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error_description?: string; error?: string } } })?.response
               ?.data?.error_description ??
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            (err as { message?: string })?.message ??
            'Failed to create worker';
         setError(msg);
      }
   };

   const handleDone = () => {
      onClose();
      onCreated();
   };

   const copyToClipboard = async (text: string, which: 'id' | 'secret') => {
      try {
         await navigator.clipboard.writeText(text);
         if (which === 'id') {
            setCopiedId(true);
            setTimeout(() => setCopiedId(false), 2000);
         } else {
            setCopiedSecret(true);
            setTimeout(() => setCopiedSecret(false), 2000);
         }
      } catch {
         // ignore
      }
   };

   const inputClassName =
      'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

   const toggleCapability = (id: string) => {
      setCapabilityIds((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   };

   return (
      <SlideDrawer isOpen={isOpen} onClose={onClose}>
                  <div className="flex h-full flex-col" data-testid="create-worker-dialog">
                  <div className="relative border-b border-slate-100 bg-slate-50/50 px-6 py-4 sm:px-8">
                     <div className="flex items-center justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                           <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                              <Server className="h-5 w-5 text-emerald-600" />
                           </div>
                           <div className="min-w-0">
                              <h2 className="text-xl font-semibold text-slate-900">
                                 {phase === 'form' ? 'Add Worker' : 'Worker Created'}
                              </h2>
                              <p className="text-sm text-slate-500">
                                 {phase === 'form'
                                    ? 'Configure this worker and grant it access to capabilities.'
                                    : 'Copy these credentials now — the secret cannot be retrieved later.'}
                              </p>
                           </div>
                        </div>
                        <button
                           type="button"
                           onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onClose();
                           }}
                           className="shrink-0 rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                        >
                           <X size={20} />
                        </button>
                     </div>
                  </div>

                  <div className="flex-1 overflow-y-auto bg-white p-6 sm:p-8">
                     {phase === 'form' ? (
                        <div className="mx-auto max-w-2xl space-y-6">
                           <div className="space-y-2">
                              <Label htmlFor="worker-name">
                                 Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                 id="worker-name"
                                 placeholder="e.g. Document Parser"
                                 value={name}
                                 onChange={(e) => setName(e.target.value)}
                                 className={inputClassName}
                                 autoFocus
                              />
                           </div>

                           <div className="space-y-2">
                              <Label htmlFor="worker-description">Description</Label>
                              <textarea
                                 id="worker-description"
                                 placeholder="What does this worker do?"
                                 value={description}
                                 onChange={(e) => setDescription(e.target.value)}
                                 rows={3}
                                 className={`${inputClassName} resize-none`}
                              />
                           </div>

                           <div className="space-y-2">
                              <Label>Type</Label>
                              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                                 {WORKER_TYPE_OPTIONS.map((opt) => (
                                    <button
                                       key={opt.value}
                                       type="button"
                                       data-testid={`worker-type-${opt.value}`}
                                       onClick={() => setWorkerType(opt.value)}
                                       className={`rounded-md border p-3 text-left text-sm transition-colors ${
                                          workerType === opt.value
                                             ? 'border-emerald-500 bg-emerald-50'
                                             : 'border-slate-200 bg-white hover:border-slate-300'
                                       }`}
                                    >
                                       <div className="font-semibold text-slate-900">{opt.label}</div>
                                       <div className="mt-0.5 text-xs text-slate-500">{opt.hint}</div>
                                    </button>
                                 ))}
                              </div>
                           </div>

                           <div className="space-y-2">
                              <Label>Allowed capabilities</Label>
                              <p className="text-xs text-slate-500">
                                 Select the capabilities this worker can run.
                              </p>
                              <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/40 p-3">
                                 {capabilitiesLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-slate-500">
                                       <Loader2 className="h-4 w-4 animate-spin" />
                                       Loading capabilities…
                                    </div>
                                 ) : capabilityOptions.length === 0 ? (
                                    <p className="text-sm text-slate-500">
                                       No capabilities available. This worker will be created with no access; you can
                                       update it later.
                                    </p>
                                 ) : (
                                    <ul className="space-y-1.5">
                                       {capabilityOptions.map((c) => {
                                          const checked = capabilityIds.has(c.id);
                                          return (
                                             <li key={c.id} className="flex items-center gap-2">
                                                <Checkbox
                                                   id={`cap-${c.id}`}
                                                   checked={checked}
                                                   onCheckedChange={() => toggleCapability(c.id)}
                                                />
                                                <Label
                                                   htmlFor={`cap-${c.id}`}
                                                   className="cursor-pointer text-sm font-normal text-slate-700"
                                                >
                                                   {c.name}
                                                   {c.app_key ? (
                                                      <span className="ml-1 text-xs text-slate-400">
                                                         · {c.app_key}
                                                      </span>
                                                   ) : null}
                                                </Label>
                                             </li>
                                          );
                                       })}
                                    </ul>
                                 )}
                              </div>
                           </div>

                           {error && <p className="text-sm text-red-600">{error}</p>}
                        </div>
                     ) : credentials ? (
                        <div className="mx-auto max-w-2xl space-y-4">
                           <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                              <p className="text-sm text-amber-800">
                                 Save this; we won&apos;t show it again. If you lose the secret you&apos;ll need to
                                 rotate it.
                              </p>
                           </div>

                           <div className="space-y-2">
                              <Label>Worker ID</Label>
                              <div className="flex gap-2">
                                 <Input
                                    readOnly
                                    value={credentials.client_id}
                                    className="font-mono text-sm"
                                    data-testid="worker-credentials-id"
                                 />
                                 <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={() => copyToClipboard(credentials.client_id, 'id')}
                                    data-testid="copy-worker-id"
                                 >
                                    {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                 </Button>
                              </div>
                           </div>

                           <div className="space-y-2">
                              <Label>Worker Secret</Label>
                              <div className="flex gap-2">
                                 <Input
                                    readOnly
                                    type="password"
                                    value={credentials.client_secret}
                                    className="font-mono text-sm"
                                    data-testid="worker-credentials-secret"
                                 />
                                 <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={() => copyToClipboard(credentials.client_secret, 'secret')}
                                    data-testid="copy-worker-secret"
                                 >
                                    {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                 </Button>
                              </div>
                           </div>

                           <p className="text-xs text-slate-500">
                              Set WORKER_CLIENT_ID and WORKER_CLIENT_SECRET on the worker process, along with
                              WORKER_TOKEN_URL pointing at your issuer.
                           </p>
                        </div>
                     ) : null}
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-6 py-4 sm:px-8">
                     {phase === 'form' ? (
                        <>
                           <Button
                              type="button"
                              variant="outline"
                              onClick={onClose}
                              disabled={createWorker.isPending}
                           >
                              Cancel
                           </Button>
                           <Button
                              type="button"
                              onClick={() => void handleSubmit()}
                              disabled={!canSubmit}
                              data-testid="create-worker-submit"
                           >
                              {createWorker.isPending ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating…
                                 </>
                              ) : (
                                 <>Create worker</>
                              )}
                           </Button>
                        </>
                     ) : (
                        <div className="ml-auto flex gap-3">
                           <Button type="button" onClick={handleDone} data-testid="create-worker-done">
                              Done
                           </Button>
                        </div>
                     )}
                  </div>
                  </div>
      </SlideDrawer>
   );
}
